import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AttachFile,
  ExpandLess,
  ExpandMore,
  Help as HelpIcon,
} from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";

import AttachmentListItem from "src/reusable/Chat/ChatAttachments/AttachmentListItem";
import { ChunkedFileWithPreview } from "../types/chunkedUploadTypes";
import { FileWithPreview } from "../types";
import { useLocation } from "react-router-dom";

type AttachmentDrawerProps = {
  files: (FileWithPreview | ChunkedFileWithPreview)[];
  onRemove?: (fileId: string) => void; // Changed from index to fileId
  isEditable?: boolean;
  pendingArtifactFileIds?: Map<string, "finalizing" | "failed">;
  isProjectChat?: boolean;
};

const AttachmentDrawer: React.FC<AttachmentDrawerProps> = ({
  files,
  onRemove,
  isEditable = false,
  pendingArtifactFileIds,
  isProjectChat = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  // Tracks whether the user has explicitly toggled the drawer this session.
  // Auto-collapse on "All ready" only fires when this is false — otherwise
  // we'd yank an open drawer shut while the user is reading it.
  const [userToggled, setUserToggled] = useState(false);
  // Track ready status from child components - keyed by file ID (not index)
  const [fileReadyStatus, setFileReadyStatus] = useState<Map<string, boolean>>(
    new Map(),
  );

  // Reset drawer expansion when navigating (e.g., clicking left sidebar "AtomAssist")
  // Note: We do NOT clear fileReadyStatus here - status should persist across navigation
  // as long as the same files are attached
  const location = useLocation();
  useEffect(() => {
    setIsExpanded(true);
    setUserToggled(false);
  }, [location.pathname]);

  // Also reset when attachments are cleared or when adding after an empty state
  const prevFilesLenRef = useRef<number>(files.length);
  useEffect(() => {
    const prevLen = prevFilesLenRef.current;
    const currLen = files.length;

    // Reset when cleared
    if (currLen === 0 && prevLen > 0) {
      setIsExpanded(true);
      setUserToggled(false);
      setFileReadyStatus(new Map());
    }

    // When transitioning from empty -> has files, default to expanded;
    // the auto-collapse effect will close it once everything is ready.
    if (prevLen === 0 && currLen > 0) {
      setIsExpanded(true);
      setUserToggled(false);
      setFileReadyStatus(new Map());
    }

    prevFilesLenRef.current = currLen;
  }, [files.length]);

  if (!files || files.length === 0) return null;

  const toggleExpanded = () => {
    setUserToggled(true);
    setIsExpanded((prev) => !prev);
  };

  const fileCountText =
    files.length === 1 ? "1 file attached" : `${files.length} files attached`;

  // Callback for children to report their status
  const handleStatusChange = React.useCallback(
    (fileId: string, isReady: boolean) => {
      setFileReadyStatus((prev) => {
        const next = new Map(prev);
        next.set(fileId, isReady);
        return next;
      });
    },
    [],
  );

  // Calculate overall status
  const overallStatus = React.useMemo(() => {
    let hasFailed = false;
    let hasUploading = false;
    let hasProcessing = false;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const chunkedFile = file as any;

      // Check upload status first
      if (chunkedFile.uploadStatus === "failed") {
        hasFailed = true;
        continue;
      }

      if (
        chunkedFile.uploadStatus === "pending" ||
        chunkedFile.uploadStatus === "uploading"
      ) {
        hasUploading = true;
        continue;
      }

      // Check processing status from child components using file ID (not index)
      // If we haven't received a status update yet (undefined), assume still processing
      // This prevents showing "All ready" before child components report their status
      const fileId =
        chunkedFile.uniqueId || chunkedFile.id || `${file.name}-${i}`;
      const artifactState = pendingArtifactFileIds?.get(fileId);
      if (artifactState === "failed") {
        hasFailed = true;
        continue;
      }
      if (artifactState === "finalizing") {
        hasProcessing = true;
        continue;
      }
      const isReady = fileReadyStatus.get(fileId);
      if (isReady === false || isReady === undefined) {
        hasProcessing = true;
      }
    }

    if (hasFailed) {
      return { label: "Some failed", color: "error" as const };
    } else if (hasUploading) {
      return { label: "Uploading...", color: "primary" as const };
    } else if (hasProcessing) {
      return { label: "Processing...", color: "secondary" as const };
    }
    return { label: "All ready", color: "success" as const };
  }, [files, fileReadyStatus, pendingArtifactFileIds]);

  const allReady = overallStatus.label === "All ready";

  // Auto-collapse once everything is ready, unless the user has explicitly
  // toggled the drawer this session (we never override a deliberate user choice).
  useEffect(() => {
    if (userToggled) return;
    setIsExpanded(!allReady);
  }, [allReady, userToggled]);

  return (
    <Paper
      variant="outlined"
      data-testid="aa-file-list"
      sx={{
        mb: 1,
        borderColor: "divider",
        backgroundColor: "background.default",
        borderRadius: 1,
        overflow: "hidden", // Keep hidden to contain the scrollable area properly
        position: "relative", // Ensure proper positioning context
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          px: 1.5,
          py: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
        onClick={toggleExpanded}
        data-attr="assistant:btn:toggle-attachments-drawer"
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <AttachFile fontSize="small" color="action" />
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={500}
            data-testid="aa-file-drawer-count"
          >
            {fileCountText}
          </Typography>
          <Tooltip
            arrow
            placement="top-start"
            enterDelay={300}
            title="Small files may be sent in full with your message. Larger files and datasets are searched for relevant content. Very large file sets may limit what gets analyzed."
          >
            <IconButton
              size="small"
              onClick={(e) => e.stopPropagation()}
              sx={{ p: 0.25, cursor: "help" }}
              aria-label="About file attachments"
            >
              <HelpIcon color="primary" sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" alignItems="center" gap={1}>
          <Typography
            variant="caption"
            color={overallStatus.color}
            sx={{ fontWeight: 600 }}
          >
            {overallStatus.label}
          </Typography>
          <IconButton size="small">
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Stack>
      </Box>

      {/* Expandable Content */}
      <Collapse in={isExpanded}>
        <Divider />
        <Box
          sx={{
            px: 1,
            py: 1,
            maxHeight: "min(300px, 25vh)", // Cap drawer height; shrinks on short viewports (e.g. 1080p)
            minHeight: files.length > 0 ? "44px" : undefined, // Ensure minimum height
            overflowY: "auto", // Enable vertical scrolling
            overflowX: "hidden", // Prevent horizontal scrolling
            position: "relative", // Ensure proper positioning
            // Force display of scrollbar when content overflows
            overscrollBehavior: "contain", // Prevent scroll chaining
            // Custom scrollbar styling - always visible when needed
            "&::-webkit-scrollbar": {
              width: "10px", // Slightly wider for better usability
              height: "10px",
              display: "block", // Ensure scrollbar is displayed
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "action.hover",
              borderRadius: "5px",
              border: "1px solid transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "action.selected",
              borderRadius: "5px",
              border: "2px solid transparent",
              backgroundClip: "padding-box",
              minHeight: "30px", // Minimum thumb height for better grabbing
              "&:hover": {
                backgroundColor: "primary.main",
                borderColor: "primary.light",
              },
              "&:active": {
                backgroundColor: "primary.dark",
              },
            },
            // Show scrollbar corner for better UX
            "&::-webkit-scrollbar-corner": {
              backgroundColor: "transparent",
            },
            // Firefox scrollbar styling
            scrollbarWidth: "thin",
            scrollbarColor: "action.selected action.hover",
            // Ensure smooth scrolling
            scrollBehavior: "smooth",
            // Add padding-right when scrollbar is visible to prevent content shift
            paddingRight: files.length > 3 ? "4px" : "12px",
          }}
        >
          <Stack spacing={0.5}>
            {files.map((file, index) => {
              // Use unique ID for key to avoid React reconciliation issues with duplicate filenames
              const fileKey =
                (file as any).uniqueId ||
                (file as any).id ||
                `${file.name}-${index}`;

              return (
                <AttachmentListItem
                  key={fileKey}
                  file={file}
                  fileId={fileKey}
                  onRemove={onRemove}
                  isEditable={isEditable}
                  onStatusChange={handleStatusChange}
                  pendingArtifact={pendingArtifactFileIds?.get(fileKey)}
                  isProjectChat={isProjectChat}
                />
              );
            })}
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default AttachmentDrawer;
